import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Conn } from '../conn';
import {
  listAgents, listAgentSkills, getAgentSkill, putAgentSkill, deleteAgentSkill,
  clawhubBrowse, clawhubSearch, clawhubFile, type SkillInfo, type ClawSkill,
} from '../api';

const SORTS: { id: string; label: string }[] = [
  { id: 'installsAllTime', label: 'Top' },
  { id: 'trending', label: 'Trending' },
  { id: 'newest', label: 'Newest' },
  { id: 'stars', label: 'Stars' },
  { id: 'downloads', label: 'Downloads' },
];
const fmt = (n?: number) => (n == null ? '0' : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

export default function SkillsPage({ conn }: { conn: Conn }) {
  const [agents, setAgents] = useState<string[]>([]);
  const [agent, setAgent] = useState('');
  const [installed, setInstalled] = useState<SkillInfo[]>([]);
  const [tab, setTab] = useState<'registry' | 'installed'>('registry');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('installsAllTime');
  const [reg, setReg] = useState<ClawSkill[]>([]);
  const [loadingReg, setLoadingReg] = useState(false);
  const [detail, setDetail] = useState<{ s: ClawSkill; content: string; loading: boolean } | null>(null);
  const [editing, setEditing] = useState<{ name: string; content: string } | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const debounce = useRef<number | null>(null);

  useEffect(() => {
    if (!conn.apiKey) { setErr('Connect an API key (sidebar).'); return; }
    listAgents(conn.baseUrl, conn.apiKey).then((a) => { setAgents(a); setAgent((c) => c || a[0] || ''); }).catch((e) => setErr(String(e)));
  }, [conn.baseUrl, conn.apiKey]);

  const loadInstalled = useCallback(async () => {
    if (!agent) return;
    try { setInstalled(await listAgentSkills(conn.baseUrl, conn.apiKey, agent)); } catch (e) { setErr(String(e)); }
  }, [conn.baseUrl, conn.apiKey, agent]);
  useEffect(() => { loadInstalled(); }, [loadInstalled]);

  // Live ClawHub registry — browse by sort, or search by query (debounced).
  const loadReg = useCallback(async (query: string, s: string) => {
    setLoadingReg(true);
    try { setReg(query.trim() ? await clawhubSearch(query.trim(), 60) : await clawhubBrowse(s, 60)); }
    catch (e) {
      // offline fallback: the bundled top-1000 snapshot
      try { const r = await fetch('/clawhub_skills.json'); setReg(await r.json()); }
      catch { setErr(`ClawHub unreachable: ${e}`); }
    } finally { setLoadingReg(false); }
  }, []);
  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => loadReg(q, sort), q ? 350 : 0);
    return () => { if (debounce.current) window.clearTimeout(debounce.current); };
  }, [q, sort, loadReg]);

  const installedSlugs = useMemo(() => new Set(installed.map((s) => s.name.replace(/\.md$/, ''))), [installed]);
  const instFiltered = useMemo(() => installed.filter((s) => s.name.toLowerCase().includes(q.toLowerCase())), [installed, q]);

  async function openDetail(s: ClawSkill) {
    setDetail({ s, content: '', loading: true });
    try { setDetail({ s, content: await clawhubFile(s.slug), loading: false }); }
    catch (e) { setDetail({ s, content: `Could not load SKILL.md: ${e}`, loading: false }); }
  }
  async function install(s: ClawSkill) {
    if (!agent) { setErr('Pick an install-target agent first.'); return; }
    setBusy(true); setErr('');
    try {
      const content = detail?.s.slug === s.slug && detail.content && !detail.loading ? detail.content : await clawhubFile(s.slug);
      await putAgentSkill(conn.baseUrl, conn.apiKey, agent, `${s.slug}.md`, content);
      setDetail(null); setToast(`Installed ${s.name} → ${agent}`); setTimeout(() => setToast(''), 2500);
      await loadInstalled();
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }
  async function openInstalled(name: string) {
    setBusy(true);
    try { const s = await getAgentSkill(conn.baseUrl, conn.apiKey, agent, name); setEditing({ name, content: s.content }); }
    catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }
  async function saveEditing() {
    if (!editing) return; let n = editing.name.trim(); if (!n.endsWith('.md')) n += '.md';
    setBusy(true);
    try { await putAgentSkill(conn.baseUrl, conn.apiKey, agent, n, editing.content); setEditing(null); setTab('installed'); await loadInstalled(); }
    catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }
  async function remove(name: string) {
    setBusy(true);
    try { await deleteAgentSkill(conn.baseUrl, conn.apiKey, agent, name); await loadInstalled(); }
    catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }

  return (
    <div className="page">
      <header className="page-head">
        <div><h1>Skills Hub</h1><div className="muted small">Browse the live <b>ClawHub</b> registry (10k+ skills) and install <code>SKILL.md</code> skills to an agent.</div></div>
        <div className="row">
          <label className="muted small">install to</label>
          <select value={agent} onChange={(e) => setAgent(e.target.value)}>{agents.map((a) => <option key={a} value={a}>{a}</option>)}</select>
        </div>
      </header>
      {err && <p className="err">{err}</p>}
      {toast && <p className="ok">✓ {toast}</p>}

      <div className="hero-search"><input placeholder="Search ClawHub skills by name or capability…" value={q} onChange={(e) => setQ(e.target.value)} /></div>

      <div className="toolbar">
        <div className="tabs">
          <button className={`tab ${tab === 'registry' ? 'active' : ''}`} onClick={() => setTab('registry')}>Registry</button>
          <button className={`tab ${tab === 'installed' ? 'active' : ''}`} onClick={() => setTab('installed')}>Installed ({installed.length})</button>
        </div>
        <span className="count-pill">{tab === 'registry' ? (loadingReg ? 'loading…' : `${reg.length} shown`) : `${instFiltered.length} on ${agent || '—'}`}</span>
        {tab === 'installed' && <button onClick={() => setEditing({ name: '', content: '' })}>+ New skill</button>}
      </div>

      {tab === 'registry' && !q && (
        <div className="chips">{SORTS.map((s) => <button key={s.id} className={`chip ${sort === s.id ? 'active' : ''}`} onClick={() => setSort(s.id)}>{s.label}</button>)}</div>
      )}

      {tab === 'registry' ? (
        reg.length === 0 ? <div className="empty">{loadingReg ? 'Loading registry…' : `No skills match “${q}”.`}</div> : (
          <div className="browse-grid">
            {reg.map((s) => {
              const on = installedSlugs.has(s.slug);
              return (
                <div key={s.slug} className="browse-card skill-card" onClick={() => openDetail(s)}>
                  <div className="bc-title"><span className="ico">✦</span>{s.name}</div>
                  <div className="bc-desc">{s.summary || 'No description.'}</div>
                  <div className="bc-meta">
                    {s.installs != null && <span className="badge accent">⤓ {fmt(s.installs)}</span>}
                    {s.stars != null && <span className="badge">★ {fmt(s.stars)}</span>}
                    {s.version && <span className="badge">v{s.version}</span>}
                  </div>
                  <div className="reqs">{s.owner ? `@${s.owner}` : 'clawhub'} · {s.slug}</div>
                  <div className="bc-actions">
                    <button className="link grow" onClick={(e) => { e.stopPropagation(); openDetail(s); }}>View</button>
                    {on ? <span className="badge green">● installed</span>
                        : <button className="link" onClick={(e) => { e.stopPropagation(); install(s); }} disabled={busy}>Install →</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        instFiltered.length === 0 ? <div className="empty">No skills on <code>{agent || '—'}</code>. Browse the <b>Registry</b> to add some.</div> : (
          <div className="browse-grid">
            {instFiltered.map((s) => (
              <div key={s.name} className="browse-card" onClick={() => openInstalled(s.name)}>
                <div className="bc-title"><span className="ico">✦</span>{s.name}</div>
                <div className="bc-desc">Attached to <code>{agent}</code></div>
                <div className="bc-meta"><span className="badge">{s.size} b</span></div>
                <div className="bc-actions">
                  <button className="link grow" onClick={(e) => { e.stopPropagation(); openInstalled(s.name); }}>Edit</button>
                  <button className="link danger" onClick={(e) => { e.stopPropagation(); remove(s.name); }}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {detail && (
        <div className="detail-overlay" onClick={() => setDetail(null)}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dm-head">
              <div className="dm-title"><span style={{ color: 'var(--accent-2)' }}>✦</span>{detail.s.name}</div>
              <button className="x-btn" onClick={() => setDetail(null)}>✕</button>
            </div>
            <div className="muted small">{detail.s.owner ? `@${detail.s.owner}` : 'clawhub'} · <code>{detail.s.slug}</code>{detail.s.version ? ` · v${detail.s.version}` : ''}</div>
            <div className="dm-meta">
              {detail.s.installs != null && <span className="badge accent">⤓ {fmt(detail.s.installs)} installs</span>}
              {detail.s.stars != null && <span className="badge">★ {fmt(detail.s.stars)}</span>}
              {detail.s.downloads != null && <span className="badge">{fmt(detail.s.downloads)} downloads</span>}
            </div>
            {detail.s.summary && <p className="muted small" style={{ marginTop: 0 }}>{detail.s.summary}</p>}
            <div className="md-body">
              {detail.loading ? <p className="muted">Loading SKILL.md…</p>
                : <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.content}</ReactMarkdown>}
            </div>
            <div className="dm-foot">
              <a className="x-btn" href={`https://clawhub.ai/${detail.s.slug}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>View on ClawHub ↗</a>
              {installedSlugs.has(detail.s.slug)
                ? <span className="badge green" style={{ alignSelf: 'center' }}>● already installed</span>
                : <button onClick={() => install(detail.s)} disabled={busy || !agent || detail.loading}>Install to {agent || '—'} →</button>}
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="panel">
          <div className="panel-head"><b>{editing.name ? <code>{editing.name}</code> : 'New SKILL.md'}</b>
            <div className="row"><button className="x-btn" onClick={() => setEditing(null)}>Cancel</button><button onClick={saveEditing} disabled={busy || !editing.name.trim() || !agent}>Save to {agent || '—'}</button></div>
          </div>
          <input placeholder="skill-name.md" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} style={{ marginBottom: 10 }} />
          <textarea className="editor" placeholder={'---\nname: my-skill\ndescription: what it does\n---\n\nWhen the user…'} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} />
        </div>
      )}
    </div>
  );
}
